import { useInfiniteQuery } from '@tanstack/vue-query';
import { computed, toValue, type ComputedRef, type Ref } from 'vue';
import http from '@/services/http';
import type { ApiResponse } from '@/types/types';

const PAGE_SIZE = 30;

export interface MultiFilterOption {
  id: number;
  owner_type: string;
  name?: string;
  display_string?: string;
}

export interface UseMultiFilterOptionsParams {
  slug: Ref<string>;
  ownerType: Ref<string>;
  searchQuery: Ref<string>;
  /** Merged with the built-in rule (non-empty trimmed search and slug). */
  enabled?: Ref<boolean> | ComputedRef<boolean>;
}

/**
 * Fetches filter options from a single API slug with infinite-scroll pagination.
 * Only fires when searchQuery is non-empty. Passes the search term to the API
 * for server-side filtering via the Elasticsearch 'contains' criterion.
 *
 * @param slug - Reactive slug for the selected owner type (e.g. 'account')
 * @param ownerType - Reactive owner type label (e.g. 'Account')
 * @param searchQuery - Reactive search query (should be debounced by the caller)
 */
export function useMultiFilterOptions({
  slug,
  ownerType,
  searchQuery,
  enabled: enabledOverride,
}: UseMultiFilterOptionsParams) {
  const searchValue = computed(() => toValue(searchQuery).trim());

  const queryKey = computed(() => ['ownerSearch', toValue(slug), searchValue.value]);

  const queryFn = async ({ pageParam }: { pageParam: string | null }): Promise<ApiResponse> => {
    const filter = {
      ruleScopes: {
        scopeType: 'and',
        rules: [
          { column: 'name', criterion: 'contains', value: searchValue.value, field: null },
        ],
      },
    };

    // Cursor (search_after) infinite scroll: null = first page; subsequent pages echo the prior
    // response's next_cursor. Count the total only on the first page.
    const response = await http.post(`/api/${toValue(slug)}/search`, {
      perPage: PAGE_SIZE,
      cursor: pageParam ?? undefined,
      withTotal: pageParam === null,
      filter,
      sort: { name: 'asc' },
    });
    return response.data as ApiResponse;
  };

  const isEnabled = computed(() => {
    const extra = enabledOverride !== undefined ? toValue(enabledOverride) : true;
    return extra && searchValue.value.length > 0 && toValue(slug).length > 0;
  });

  const query = useInfiniteQuery({
    queryKey,
    queryFn,
    initialPageParam: null as string | null,
    // Stop when the BE reports no next page (null token) or returns a short page (the true end),
    // so a final full page that still carries a token can't trigger an extra/looping empty fetch.
    getNextPageParam: (lastPage: ApiResponse) =>
      (lastPage.data?.length ?? 0) < PAGE_SIZE ? undefined : (lastPage.meta?.next_cursor ?? undefined),
    retry: 0,
    enabled: isEnabled,
  });

  const options = computed((): MultiFilterOption[] => {
    const ownerTypeValue = toValue(ownerType);
    if (!query.data.value) return [];
    return query.data.value.pages.flatMap((page: ApiResponse) =>
      (page.data || []).map((item: { id: number; name?: string; display_string?: string }) => ({
        id: item.id,
        owner_type: ownerTypeValue,
        name: item.name,
        display_string: item.display_string || item.name,
      }))
    );
  });

  const totalResults = computed(() => {
    if (!query.data.value?.pages.length) return 0;
    return query.data.value.pages[0].meta?.total ?? 0;
  });

  return {
    ...query,
    options,
    totalResults,
  };
}
